<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getLanguageService } from '@/core/services/language.service.js'
import { useLanguageStore } from '@/core/stores/language.store.js'
import { useAuthStore } from '@/modules/auth/stores/auth.store.js'
import flagDe from '@/shared/assets/svg/flags/de.svg'
import flagJp from '@/shared/assets/svg/flags/jp.svg'
import flagBr from '@/shared/assets/svg/flags/new-br.svg'
import flagCn from '@/shared/assets/svg/flags/new-cn.svg'
import flagEl from '@/shared/assets/svg/flags/new-el.svg'
import flagEs from '@/shared/assets/svg/flags/new-es.svg'
import flagGb from '@/shared/assets/svg/flags/new-gb.svg'
import { removeAccents } from '@/shared/utils/string.utils'

const { t } = useI18n()

const NEW_FLAG_MAP = {
  'en': flagGb,
  'pt-br': flagBr,
  'es-la': flagEs,
  'zh-cn': flagCn,
  'el': flagEl,
  'de': flagDe,
  'ja': flagJp,
}

const isOpen = ref(false)
const menu = ref()
const languageStore = useLanguageStore()
const authStore = useAuthStore()
const searchTerm = ref('')
const isLoading = ref(false)

const languages = computed(() =>
  languageStore.getActiveLocales.map(l => ({
    ...l,
    label: l.displayLabel ?? t(l.label),
    flag: NEW_FLAG_MAP[l.key] ?? flagGb,
  })),
)

const currentLanguageLabel = computed(() => {
  const locale = languageStore.availableLocales.find(l => l.key === languageStore.currentLanguage)
  return locale?.name || t('common.languages.default')
})

const currentFlag = computed(() => NEW_FLAG_MAP[languageStore.currentLanguage] ?? flagGb)

function displayContextMenu(event) {
  if (isLoading.value)
    return
  menu.value.toggle(event)
}

async function handleLanguageClick(lang) {
  if (lang === languageStore.currentLanguage)
    return

  languageStore.setLanguage(lang)
  if (authStore.userData?.id) {
    try {
      isLoading.value = true
      menu.value.hide()
      const languageService = getLanguageService()
      await languageService.updateDefaultLanguage(authStore.userData.id, lang)
      location.reload()
    }
    catch (error) {
      console.error('Erro ao atualizar idioma no backend:', error)
      isLoading.value = false
    }
  }
}

function popoverShow() {
  isOpen.value = true
}

function popoverHide() {
  isOpen.value = false
}

function isCurrentLanguage(langKey) {
  return langKey === languageStore.currentLanguage
}

const filteredLanguages = computed(() => {
  if (!searchTerm.value)
    return languages.value

  const normalizedSearch = removeAccents(searchTerm.value)

  return languages.value.filter(language =>
    removeAccents(language.label).includes(normalizedSearch),
  )
})
</script>

<template>
  <template v-if="!languageStore.isSingleLanguage">
    <button
      class="w-24 flex px-3 py-2.5 gap-2 items-center bg-white dark:bg-dss-surface-100 hover:brightness-90 rounded-lg focus:outline-none focus:ring-0 transition-filter duration-200 cursor-pointer"
      :class="{ 'brightness-90': isOpen }"
      aria-haspopup="listbox"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="lang-popover"
      @click="displayContextMenu($event)"
    >
      <div class="flex gap-2 items-center">
        <div>
          <img
            :src="currentFlag"
            :alt="$t('common.languages.flag-alt')"
            class=""
          >
        </div>
        <span class="text-[0.938rem] font-semibold uppercase leading-[normal]">
          {{ currentLanguageLabel }}
        </span>
      </div>
      <div
        v-if="isLoading"
        class="flex items-center justify-center"
      >
        <i class="pi pi-spin pi-spinner text-[0.625rem] text-primary-500" />
      </div>
      <i
        v-else
        class="pi pi-chevron-down text-[0.625rem] transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <Popover
      id="lang-popover"
      ref="menu"
      class="w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-dss-surface-50 before:hidden after:hidden mt-2! rounded-2xl! border! border-dss-surface-200 shadow-[0px_4px_6px_-4px_#0000001A,0px_10px_15px_-3px_#0000001A] font-inter"
      :pt="{ content: { class: 'p-2' } }"
      @show="popoverShow"
      @hide="popoverHide"
    >
      <div class="flex flex-col gap-2">
        <dss-input-text
          v-model="searchTerm"
          icon="pi-search"
          input-class="h-8 border-none focus:ring-0 placeholder:text-sm placeholder:text-dss-surface-850"
          :placeholder="t('common.languages.search')"
        />

        <hr class="-mx-2 border-dss-surface-200">

        <div>
          <span class="block my-2 mx-3 text-dss-surface-850 text-xs font-medium uppercase leading-[normal]">
            {{ t('common.languages.title') }}
          </span>

          <ul class="">
            <li
              v-for="language in filteredLanguages"
              :key="language.key"
              class="group"
              :class="[isCurrentLanguage(language.key) ? 'cursor-default' : 'cursor-pointer']"
              @click="handleLanguageClick(language.key)"
            >
              <div
                class="flex min-w-0 items-center gap-3 py-2 px-3 rounded-lg transition-colors duration-200"
                :class="[isCurrentLanguage(language.key) ? '' : 'hover:bg-dss-surface-60']"
              >
                <div class="w-5 h-5 shrink-0">
                  <img
                    :src="language.flag"
                    :alt="$t('common.languages.flag-alt')"
                    class="size-5"
                  >
                </div>
                <span
                  class="min-w-0 truncate text-sm font-medium"
                  :title="language.label"
                  :class="[isCurrentLanguage(language.key) ? '' : 'group-hover:underline']"
                >
                  {{ language.label }}
                </span>
              </div>
            </li>

            <li
              v-if="!filteredLanguages?.length"
              class="py-2 px-3 text-center text-sm text-dss-surface-500"
            >
              {{ t('common.languages.no-results') }}
            </li>
          </ul>
        </div>
      </div>
    </Popover>
  </template>
</template>
